import React, { createContext, useContext, useMemo } from 'react';

import {
  TGroupedResourceCounts,
  TGroupedResourceCountsContextValue,
  TGroupedResourceCountsProviderProps,
} from '@/console/features/groups/hooks/use-grouped-resource-counts/grouped-resource-counts.types';
import { useReadGroupedAssetCountsQuery } from '@/shared/redux/api/assets/groups/groups-api';
import { useReadGroupedIdentityCountsQuery } from '@/shared/redux/api/ident/ident-api';
import { useReadGroupedIntegrationCountsQuery } from '@/shared/redux/api/integration/integrations-api';
import { useReadGroupedPipelineCountsQuery } from '@/shared/redux/api/jobs/jobs-api';

const EMPTY_COUNTS: TGroupedResourceCounts = {
  devices: 0,
  projects: 0,
  gateways: 0,
  documents: 0,
  vaults: 0,
  licenses: 0,
  subGroups: 0,
  pipelines: 0,
  users: 0,
  userRoles: 0,
  integrations: 0,
};

const GroupedResourceCountsContext =
  createContext<TGroupedResourceCountsContextValue>({
    isEnabled: false,
    isLoading: false,
    isFetching: false,
    getCounts: () => undefined,
  });

// Fetches every group's resource counts (assets, identities, pipelines, integrations)
// in four batched queries instead of the per-group-row fan-out, then serves each row
// from the result. Kills the home "recent groups" N+1.
export const GroupedResourceCountsProvider: React.FC<
  TGroupedResourceCountsProviderProps
> = ({ groupIds, children }) => {
  const options = { skip: groupIds.length === 0 };
  const {
    data: assets,
    isLoading: assetsLoading,
    isFetching: assetsFetching,
  } = useReadGroupedAssetCountsQuery({ groupIds }, options);
  const {
    data: identities,
    isLoading: identitiesLoading,
    isFetching: identitiesFetching,
  } = useReadGroupedIdentityCountsQuery({ groupIds }, options);
  const {
    data: pipelines,
    isLoading: pipelinesLoading,
    isFetching: pipelinesFetching,
  } = useReadGroupedPipelineCountsQuery({ groupIds }, options);
  const {
    data: integrations,
    isLoading: integrationsLoading,
    isFetching: integrationsFetching,
  } = useReadGroupedIntegrationCountsQuery({ groupIds }, options);

  const value = useMemo<TGroupedResourceCountsContextValue>(() => {
    const byGroup = new Map<string, TGroupedResourceCounts>();
    const ensure = (groupId: string): TGroupedResourceCounts => {
      let counts = byGroup.get(groupId);
      if (!counts) {
        counts = { ...EMPTY_COUNTS };
        byGroup.set(groupId, counts);
      }
      return counts;
    };

    assets?.readGroupedAssetCounts.forEach((count) => {
      const target = ensure(count.groupId);
      target.devices = count.devices;
      target.projects = count.projects;
      target.gateways = count.gateways;
      target.documents = count.documents;
      target.vaults = count.vaults;
      target.licenses = count.licenses;
      target.subGroups = count.subGroups;
    });
    identities?.readGroupedIdentityCounts.forEach((count) => {
      const target = ensure(count.groupId);
      target.users = count.users;
      target.userRoles = count.userRoles;
    });
    pipelines?.readGroupedPipelineCounts.forEach((count) => {
      ensure(count.groupId).pipelines = count.count;
    });
    integrations?.readGroupedIntegrationCounts.forEach((count) => {
      ensure(count.groupId).integrations = count.count;
    });

    return {
      isEnabled: true,
      isLoading:
        assetsLoading ||
        identitiesLoading ||
        pipelinesLoading ||
        integrationsLoading,
      isFetching:
        assetsFetching ||
        identitiesFetching ||
        pipelinesFetching ||
        integrationsFetching,
      getCounts: (groupId: string) => byGroup.get(groupId),
    };
  }, [
    assets,
    identities,
    pipelines,
    integrations,
    assetsLoading,
    identitiesLoading,
    pipelinesLoading,
    integrationsLoading,
    assetsFetching,
    identitiesFetching,
    pipelinesFetching,
    integrationsFetching,
  ]);

  return (
    <GroupedResourceCountsContext.Provider value={value}>
      {children}
    </GroupedResourceCountsContext.Provider>
  );
};

export const useGroupedResourceCountsContext = () =>
  useContext(GroupedResourceCountsContext);
